import { useMemo } from 'react';
import L from 'leaflet';
import { CultureIndexMapData } from 'types/Meteorology/Agro/CultureIndex/Area/getMapData';
import { useGetCultureIndexAreaMapConfigQuery } from 'services/api/Meteorology/Agro/CultureIndex/Area';

const addLegendDivFromRanges = (
  ranges: Array<{ min: number; max: number | null; color: string; label: string }>,
  title: string,
) => () => {
  const div = L.DomUtil.create('div', 'heatmap-legend');
  div.innerHTML = `<div class="text-nowrap">${title}</div>`;

  ranges.forEach((range) => {
    const rangeText = range.max === null
      ? `${range.min}+`
      : `${range.min}&ndash;${range.max}`;
    div.innerHTML += `<i style="background:${range.color}"></i> ${rangeText} - ${range.label}<br>`;
  });

  return div;
};

const addLegendDiv = (
  values: string[],
  gradient: Record<number, string>,
  label: string,
) => () => {
  const colorValues = [
    '0',
    ...Object.keys(gradient).sort((a, b) => parseFloat(a) - parseFloat(b)),
  ];
  const div = L.DomUtil.create('div', 'heatmap-legend');
  div.innerHTML = `<div class="text-nowrap">${label}</div>`;
  for (let i = 0; i < colorValues.length; i += 1) {
    if (i === 0) {
      div.innerHTML += `<i style="background:${gradient[parseFloat(colorValues[1])]}"></i> ${values[0]}&ndash;${values[1]}<br>`;
    } else if (i === colorValues.length - 1) {
      div.innerHTML += `<i style="background:${gradient[parseFloat(colorValues[i])]}"></i> ${values[i]}+<br>`;
    } else {
      const currentColor = gradient[parseFloat(colorValues[i])];
      const nextColor = gradient[parseFloat(colorValues[i + 1])];
      div.innerHTML += `<i style="background:linear-gradient(180deg,${currentColor} 0%, ${nextColor} 100%);"></i> ${values[i]}&ndash;${values[i + 1]}<br>`;
    }
  }
  return div;
};

export default function useHeatmapLegend(
  data?: CultureIndexMapData,
  gradient?: Record<number, string>,
) {
  const { data: configData } = useGetCultureIndexAreaMapConfigQuery();

  return useMemo(() => {
    if (!data || !data.length || !gradient) return undefined;

    const legend = new L.Control({ position: 'bottomright' });

    // Se temos legendRanges da API, usamos eles
    const legendRanges = configData?.variaveis?.[0]?.config?.legendRanges;
    if (legendRanges && legendRanges.length > 0) {
      legend.onAdd = addLegendDivFromRanges(legendRanges, 'Índice [adm]');
      return legend;
    }

    // Fallback para o comportamento antigo
    const values = data.map(([,, value]) => value);
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);

    const unnormalizedColorValues = Object.keys(gradient).map(Number).map((key) => (
      key * (maxValue - minValue) + minValue
    ));

    const legendValues = [
      0,
      ...unnormalizedColorValues.sort((a, b) => a - b),
    ].map((value) => value.toFixed(1));

    legend.onAdd = addLegendDiv(legendValues, gradient, 'Índice [adm]');

    return legend;
  }, [data, gradient, configData]);
}
